<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    header="Pair Agent"
    :dismissable="false"
    :style="{ width: '28rem' }"
  >
    <div class="py-6 text-center">
      <p class="text-navy-500 mb-6 text-sm">
        Install the Trellis Agent on a Windows machine on your network, then
        enter the pairing code below when prompted.
      </p>
      <div class="mb-4 inline-block rounded-lg bg-neutral-100 px-8 py-4">
        <span
          class="text-navy-800 text-3xl font-bold tracking-widest"
          style="font-family: monospace"
        >
          {{ code }}
        </span>
      </div>
      <div
        class="text-sm"
        :class="countdownUrgent ? 'text-red-600' : 'text-navy-500'"
      >
        Expires in {{ countdownDisplay }}
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <AppButton variant="secondary" @click="handleCancel">
          Cancel
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { computed, onUnmounted, ref, watch } from "vue"

import type { AgentRegistrationType } from "../../pages/Company/types"
import POLL_AGENT_STATUS from "../../queries/agent_registration/pollStatus.gql"
import { apolloClient } from "../../queries/client"
import { subscribeToCompany, type AblySubscription } from "../../services/cable"
import AppButton from "../ui/AppButton.vue"

type AgentRegistrationUpdatedPayload = {
  type: string
  agent_registration_id: string
  agent_registration_gid: string
  status: string
  name?: string | null
  hostname?: string | null
  ip_address?: string | null
  agent_version?: string | null
  last_heartbeat_at?: string | null
}

interface Props {
  code: string | null
  expiresAt: string | null
  companyId: string
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  paired: [registration: AgentRegistrationType]
  cancel: []
}>()

// ─── Countdown ──────────────────────────────────────────────────

const countdownSeconds = ref(0)
let countdownInterval: ReturnType<typeof setInterval> | null = null

const countdownDisplay = computed(() => {
  const mins = Math.floor(countdownSeconds.value / 60)
  const secs = countdownSeconds.value % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
})

const countdownUrgent = computed(() => countdownSeconds.value < 60)

const startCountdown = () => {
  stopCountdown()
  if (!props.expiresAt) return

  const expiresAtMs = new Date(props.expiresAt).getTime()

  const tick = () => {
    const remaining = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000))
    countdownSeconds.value = remaining
    if (remaining <= 0) {
      stopCountdown()
      emit("cancel")
    }
  }

  tick()
  countdownInterval = setInterval(tick, 1000)
}

const stopCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

// ─── WebSocket Subscription ─────────────────────────────────────

let subscription: AblySubscription | null = null

const fetchFullRegistration = async (
  registrationId: string,
): Promise<AgentRegistrationType | null> => {
  try {
    const result = await apolloClient.query({
      query: POLL_AGENT_STATUS,
      variables: { companyId: props.companyId },
      fetchPolicy: "network-only",
    })
    const reg = result.data?.company?.agentRegistration
    if (!reg || reg.id !== registrationId) return null
    return reg as AgentRegistrationType
  } catch (err) {
    console.error("Failed to fetch full agent registration:", err)
    return null
  }
}

const startSubscription = async () => {
  if (subscription) return

  subscription = await subscribeToCompany<AgentRegistrationUpdatedPayload>(
    props.companyId,
    (data) => {
      if (data?.type !== "agent_registration_updated") return
      if (data.status !== "configuring") return

      void (async () => {
        const full = await fetchFullRegistration(data.agent_registration_id)
        if (full) emit("paired", full)
      })()
    },
  )
}

const stopSubscription = () => {
  if (subscription) {
    subscription.close()
    subscription = null
  }
}

// ─── Lifecycle ──────────────────────────────────────────────────

watch(visible, (isVisible) => {
  if (isVisible) {
    startCountdown()
    void startSubscription()
  } else {
    stopCountdown()
    stopSubscription()
  }
})

watch(
  () => props.expiresAt,
  () => {
    if (visible.value) startCountdown()
  },
)

onUnmounted(() => {
  stopCountdown()
  stopSubscription()
})

// ─── Cancel ─────────────────────────────────────────────────────

const handleCancel = () => {
  emit("cancel")
}
</script>
